var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/deserializer/createDeserializeMdPlugin.ts
import { createPluginFactory, isUrl } from "@udecode/plate-common";

// src/remark-slate/remarkDefaultElementRules.ts
import { ELEMENT_BLOCKQUOTE } from "@udecode/plate-block-quote";
import {
  ELEMENT_CODE_BLOCK,
  ELEMENT_CODE_LINE
} from "@udecode/plate-code-block";
import {
  getPluginType as getPluginType2
} from "@udecode/plate-common";
import {
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6
} from "@udecode/plate-heading";
import { ELEMENT_HR } from "@udecode/plate-horizontal-rule";
import { ELEMENT_LINK } from "@udecode/plate-link";
import {
  ELEMENT_LI,
  ELEMENT_LIC,
  ELEMENT_OL,
  ELEMENT_UL
} from "@udecode/plate-list";
import { ELEMENT_IMAGE } from "@udecode/plate-media";
import { ELEMENT_PARAGRAPH } from "@udecode/plate-paragraph";

// src/remark-slate/remarkTextTypes.ts
var remarkTextTypes = [
  "emphasis",
  "strong",
  "delete",
  "inlineCode",
  "html",
  "text"
];

// src/remark-slate/remarkTransformElement.ts
var remarkTransformElement = (node, options) => {
  const { elementRules } = options;
  const { type } = node;
  const elementRule = elementRules[type];
  if (!elementRule)
    return [];
  return elementRule.transform(node, options);
};

// src/remark-slate/remarkDefaultTextRules.ts
import { MARK_BOLD, MARK_CODE, MARK_ITALIC } from "@udecode/plate-basic-marks";
import { getPluginType } from "@udecode/plate-common";
var remarkDefaultTextRules = {
  text: {},
  emphasis: { mark: ({ editor }) => getPluginType(editor, MARK_ITALIC) },
  strong: { mark: ({ editor }) => getPluginType(editor, MARK_BOLD) },
  inlineCode: { mark: ({ editor }) => getPluginType(editor, MARK_CODE) },
  html: { transform: (text) => text.replaceAll("<br>", "\n") }
};

// src/remark-slate/remarkTransformText.ts
var remarkTransformText = (node, options, inheritedMarkProps = {}) => {
  const { editor, textRules } = options;
  const { type, value, children } = node;
  const textRule = textRules[type] || remarkDefaultTextRules.text;
  const { mark, transform = (text) => text } = textRule;
  const markProps = mark ? __spreadProps(__spreadValues({}, inheritedMarkProps), {
    [mark({ editor })]: true
  }) : inheritedMarkProps;
  const childTextNodes = (children == null ? void 0 : children.flatMap(
    (child) => remarkTransformText(child, options, markProps)
  )) || [];
  const currentTextNodes = value || childTextNodes.length === 0 ? [__spreadValues({ text: transform(value || "") }, markProps)] : [];
  return [...currentTextNodes, ...childTextNodes];
};

// src/remark-slate/remarkTransformNode.ts
var remarkTransformNode = (node, options) => {
  const { type } = node;
  if (remarkTextTypes.includes(type)) {
    return remarkTransformText(node, options);
  }
  return remarkTransformElement(node, options);
};

// src/remark-slate/remarkTransformElementChildren.ts
var remarkTransformElementChildren = (node, options) => {
  const { children } = node;
  if (!children)
    return [];
  return children.flatMap((child) => remarkTransformNode(child, options));
};

// src/remark-slate/remarkDefaultElementRules.ts
var remarkDefaultElementRules = {
  heading: {
    transform: (node, options) => {
      var _a;
      const headingType = {
        1: ELEMENT_H1,
        2: ELEMENT_H2,
        3: ELEMENT_H3,
        4: ELEMENT_H4,
        5: ELEMENT_H5,
        6: ELEMENT_H6
      }[(_a = node.depth) != null ? _a : 1];
      return {
        type: getPluginType2(options.editor, headingType),
        children: remarkTransformElementChildren(node, options)
      };
    }
  },
  list: {
    transform: (node, options) => ({
      type: getPluginType2(
        options.editor,
        node.ordered ? ELEMENT_OL : ELEMENT_UL
      ),
      children: remarkTransformElementChildren(node, options)
    })
  },
  listItem: {
    transform: (node, options) => ({
      type: getPluginType2(options.editor, ELEMENT_LI),
      children: remarkTransformElementChildren(node, options).map(
        (child) => __spreadProps(__spreadValues({}, child), {
          type: child.type === getPluginType2(options.editor, ELEMENT_PARAGRAPH) ? getPluginType2(options.editor, ELEMENT_LIC) : child.type
        })
      )
    })
  },
  paragraph: {
    transform: (node, options) => {
      const children = remarkTransformElementChildren(node, options);
      const paragraphType = getPluginType2(options.editor, ELEMENT_PARAGRAPH);
      const splitBlockTypes = /* @__PURE__ */ new Set([
        getPluginType2(options.editor, ELEMENT_IMAGE)
      ]);
      const elements = [];
      let inlineNodes = [];
      const flushInlineNodes = () => {
        if (inlineNodes.length > 0) {
          elements.push({
            type: paragraphType,
            children: inlineNodes
          });
          inlineNodes = [];
        }
      };
      children.forEach((child) => {
        const { type } = child;
        if (type && splitBlockTypes.has(type)) {
          flushInlineNodes();
          elements.push(child);
        } else {
          inlineNodes.push(child);
        }
      });
      flushInlineNodes();
      return elements;
    }
  },
  link: {
    transform: (node, options) => ({
      type: getPluginType2(options.editor, ELEMENT_LINK),
      url: node.url,
      children: remarkTransformElementChildren(node, options)
    })
  },
  image: {
    transform: (node, options) => ({
      type: getPluginType2(options.editor, ELEMENT_IMAGE),
      children: [{ text: "" }],
      url: node.url,
      caption: [{ text: node.alt }]
    })
  },
  blockquote: {
    transform: (node, options) => {
      return {
        type: getPluginType2(options.editor, ELEMENT_BLOCKQUOTE),
        children: node.children.flatMap(
          (paragraph) => remarkTransformElementChildren(paragraph, options)
        )
      };
    }
  },
  code: {
    transform: (node, options) => {
      var _a;
      return {
        type: getPluginType2(options.editor, ELEMENT_CODE_BLOCK),
        lang: (_a = node.lang) != null ? _a : void 0,
        children: (node.value || "").split("\n").map((line) => ({
          type: getPluginType2(options.editor, ELEMENT_CODE_LINE),
          children: [{ text: line }]
        }))
      };
    }
  },
  thematicBreak: {
    transform: (node, options) => ({
      type: getPluginType2(options.editor, ELEMENT_HR),
      children: [{ text: "" }]
    })
  }
};

// src/remark-slate/remarkPlugin.ts
function remarkPlugin(options) {
  const compiler = (node) => {
    return node.children.flatMap(
      (child) => remarkTransformNode(child, options)
    );
  };
  this.Compiler = compiler;
}

// src/deserializer/utils/deserializeMd.ts
import { getPluginOptions } from "@udecode/plate-common";
import markdown from "remark-parse";
import unified from "unified";
var deserializeMd = (editor, data) => {
  const { elementRules, textRules } = getPluginOptions(
    editor,
    KEY_DESERIALIZE_MD
  );
  const tree = unified().use(markdown).use(remarkPlugin, {
    editor,
    elementRules,
    textRules
  }).processSync(data);
  return tree.result;
};

// src/deserializer/utils/filterBreakLines.ts
var filterBreaklines = (item) => !item.text;

// src/deserializer/createDeserializeMdPlugin.ts
var KEY_DESERIALIZE_MD = "deserializeMd";
var createDeserializeMdPlugin = createPluginFactory({
  key: KEY_DESERIALIZE_MD,
  then: (editor) => ({
    editor: {
      insertData: {
        format: "text/plain",
        query: ({ data, dataTransfer }) => {
          const htmlData = dataTransfer.getData("text/html");
          if (htmlData)
            return false;
          const { files } = dataTransfer;
          if (!(files == null ? void 0 : files.length) && // if content is simply a URL pass through to not break LinkPlugin
          isUrl(data)) {
            return false;
          }
          return true;
        },
        getFragment: ({ data }) => deserializeMd(editor, data)
      }
    }
  }),
  options: {
    elementRules: remarkDefaultElementRules,
    textRules: remarkDefaultTextRules
  }
});
export {
  KEY_DESERIALIZE_MD,
  createDeserializeMdPlugin,
  deserializeMd,
  filterBreaklines,
  remarkDefaultElementRules,
  remarkDefaultTextRules,
  remarkPlugin,
  remarkTextTypes,
  remarkTransformElement,
  remarkTransformElementChildren,
  remarkTransformNode,
  remarkTransformText
};
//# sourceMappingURL=index.mjs.map